import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';

import { BUTTON_TYPES } from '@savgroup-front-common/constants';
import { Button } from '@savgroup-front-common/core';

import {
  BARCODE_DETECTOR_FORMATS,
  BARCODE_SCAN_DEDUPE_MS,
  getBarcodeDetectorConstructor,
  type BarcodeDetectorInstance,
} from '../../utils/barcodeDetector';
import {
  $ScanActions,
  $ScanBackdrop,
  $ScanMessage,
  $ScanPanel,
  $ScanTitle,
  $ScanVideo,
} from './MobileBarcodeScanModal.styles';
import messages from './messages';

export interface MobileBarcodeScanModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCodeDetected: (code: string) => void;
}

const MobileBarcodeScanModal = ({ isOpen, onClose, onCodeDetected }: MobileBarcodeScanModalProps) => {
  const intl = useIntl();
  const [cameraError, setCameraError] = useState('');
  const [cameraInfo, setCameraInfo] = useState('');
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const lastScanDedupeRef = useRef<{ value: string; time: number } | null>(null);
  const onCodeDetectedRef = useRef(onCodeDetected);
  const onCloseRef = useRef(onClose);

  onCodeDetectedRef.current = onCodeDetected;
  onCloseRef.current = onClose;

  const handleCodeDetected = useCallback((rawCode: string) => {
    const code = rawCode.trim();

    if (!code) {
      return;
    }

    const now = Date.now();
    const last = lastScanDedupeRef.current;

    if (last && last.value === code && now - last.time < BARCODE_SCAN_DEDUPE_MS) {
      return;
    }

    lastScanDedupeRef.current = { value: code, time: now };
    onCodeDetectedRef.current(code);
    onCloseRef.current();
  }, []);

  useEffect(() => {
    if (!isOpen) {
      lastScanDedupeRef.current = null;
      setCameraError('');
      setCameraInfo('');

      return undefined;
    }

    let isCancelled = false;
    const streamHolder: { current: MediaStream | null } = { current: null };

    const cancelFrame = () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };

    const releaseCamera = () => {
      cancelFrame();

      if (streamHolder.current) {
        streamHolder.current.getTracks().forEach((track) => track.stop());
        streamHolder.current = null;
      }

      const video = videoRef.current;

      if (video) {
        video.srcObject = null;
      }
    };

    const scheduleDetect = (detector: BarcodeDetectorInstance) => {
      const step = () => {
        if (isCancelled) {
          return;
        }

        const video = videoRef.current;

        if (!video) {
          rafRef.current = requestAnimationFrame(step);

          return;
        }

        if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
          detector
            .detect(video)
            .then((codes) => {
              if (isCancelled) {
                return;
              }

              const raw = codes[0]?.rawValue;

              if (raw) {
                handleCodeDetected(raw);

                return;
              }

              rafRef.current = requestAnimationFrame(step);
            })
            .catch(() => {
              if (!isCancelled) {
                rafRef.current = requestAnimationFrame(step);
              }
            });
        } else {
          rafRef.current = requestAnimationFrame(step);
        }
      };

      rafRef.current = requestAnimationFrame(step);
    };

    const run = async () => {
      setCameraError('');
      setCameraInfo('');

      if (!navigator.mediaDevices?.getUserMedia) {
        setCameraError(intl.formatMessage(messages.cameraUnavailable));

        return;
      }

      let stream: MediaStream;

      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        });
      } catch {
        setCameraError(intl.formatMessage(messages.cameraPermissionDenied));

        return;
      }

      if (isCancelled) {
        stream.getTracks().forEach((track) => track.stop());

        return;
      }

      streamHolder.current = stream;
      const video = videoRef.current;

      if (video) {
        video.setAttribute('playsinline', 'true');
        video.playsInline = true;
        video.muted = true;
        video.srcObject = stream;

        try {
          await video.play();
        } catch {
          // lecture vidéo peut échouer selon le navigateur
        }
      }

      if (isCancelled) {
        releaseCamera();

        return;
      }

      const BarcodeDetectorCtor = getBarcodeDetectorConstructor();

      if (!BarcodeDetectorCtor) {
        setCameraInfo(intl.formatMessage(messages.detectorUnsupported));

        return;
      }

      try {
        const detector = new BarcodeDetectorCtor({
          formats: [...BARCODE_DETECTOR_FORMATS],
        });

        scheduleDetect(detector);
      } catch {
        setCameraInfo(intl.formatMessage(messages.detectorInitFailed));
      }
    };

    void run();

    return () => {
      isCancelled = true;
      releaseCamera();
    };
  }, [handleCodeDetected, intl, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCloseRef.current();
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  if (!isOpen || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <$ScanBackdrop onClick={onClose} role="presentation">
      <$ScanPanel onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true">
        <$ScanTitle>{intl.formatMessage(messages.title)}</$ScanTitle>
        {cameraError ? <$ScanMessage $variant="error">{cameraError}</$ScanMessage> : null}
        {cameraInfo ? <$ScanMessage $variant="info">{cameraInfo}</$ScanMessage> : null}
        <$ScanVideo ref={videoRef} autoPlay muted playsInline />
        <$ScanActions>
          <Button type={BUTTON_TYPES.BUTTON} tertiary onClick={onClose}>
            {intl.formatMessage(messages.close)}
          </Button>
        </$ScanActions>
      </$ScanPanel>
    </$ScanBackdrop>,
    document.body,
  );
};

export default MobileBarcodeScanModal;
